/**
 * Platform admin — Chat Monitor
 *
 * Left  — filterable feedback list (only conversations that received feedback)
 * Right — selected conversation: the specific user+agent pair returned by the backend,
 *         feedback badge, agent steps toggle, and an Analyze / Copy / Export toolbar.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  useListPlatformChatFeedbackQuery,
  useGetPlatformChatConversationQuery,
  useGetPlatformConversationRunsQuery,
  useListLlmModelsQuery,
  useAnalyzeConversationTurnMutation,
  type ChatFeedbackEntry,
  type PlatformChatMessage,
  type PlatformConversation,
} from '@/redux/internal-services/platformAdminApi';
import { AgentStepsView } from '@/components/chat/AgentStepsView';
import type { AgentRunStep } from '@/redux/internal-services/agentApi';

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function shortId(id?: string | null) {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

// ── Platform-aware turn steps ─────────────────────────────────────────────────

const PlatformTurnSteps: React.FC<{ conversationId: string; targetStepId?: string }> = ({
  conversationId, targetStepId,
}) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useGetPlatformConversationRunsQuery(
    { conversationId },
    { skip: !open },
  );
  const runs = (data?.data ?? []) as { run_id?: string; steps?: AgentRunStep[] }[];

  // Build flattened steps with their reconstructed "{run_id}:a:{i}" IDs for target matching
  const stepsWithRecon = runs.flatMap((r) =>
    (r.steps ?? []).map((step, si) => ({ step, reconId: `${r.run_id}:a:${si}` }))
  );
  const steps = stepsWithRecon.map((s) => s.step);
  const targetIndex = targetStepId
    ? stepsWithRecon.findIndex((s) => s.reconId === targetStepId)
    : -1;

  const scrollRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || isFetching) return;
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open, isFetching, targetStepId]);

  return (
    <div className="mt-1 ml-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-ui-text3 hover:text-ui-text2 transition"
      >
        <span className={`transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        {open ? 'Hide agent steps' : 'View agent steps'}
      </button>
      {open && (
        <div ref={scrollRef} className="mt-1.5 max-h-64 overflow-y-auto">
          {isFetching && <p className="text-[11px] text-ui-text2 py-1">Loading steps…</p>}
          {isError && <p className="text-[11px] text-red-500 py-1">Couldn't load steps.</p>}
          {!isFetching && !isError && (
            <AgentStepsView
              steps={steps}
              targetIndex={targetIndex >= 0 ? targetIndex : undefined}
              targetRef={targetRef}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── Analyze panel ─────────────────────────────────────────────────────────────

const AnalyzePanel: React.FC<{
  conversationId: string;
  messageId: string;
}> = ({ conversationId, messageId }) => {
  const [selectedModel, setSelectedModel] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const { data: modelsData, isLoading: modelsLoading } = useListLlmModelsQuery();
  const models = modelsData?.data ?? [];

  const [analyze, { isLoading: analyzing }] = useAnalyzeConversationTurnMutation();

  const handleRun = async () => {
    if (!selectedModel) return;
    setResult(null);
    try {
      const res = await analyze({
        conversationId,
        messageId,
        modelId: selectedModel,
        userPrompt: userPrompt.trim() || null,
      }).unwrap();
      setResult(res.data.analysis);
    } catch {
      setResult('Analysis failed. Check the backend logs.');
    }
  };

  return (
    <div className="border-t border-ui-border2 bg-ui-card px-4 py-3 space-y-3 flex-shrink-0">
      <p className="text-xs font-semibold text-ui-text2">Analyze with LLM</p>

      {/* Model selector */}
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-ui-text3 whitespace-nowrap">Model</label>
        {modelsLoading ? (
          <span className="text-[11px] text-ui-text3">Loading models…</span>
        ) : (
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="flex-1 text-xs bg-ui-input border border-ui-border rounded-lg px-2 py-1 text-ui-text focus:outline-none focus:ring-1 focus:ring-brand-dark"
          >
            <option value="">— Select model —</option>
            {models.map((m) => (
              <option key={m.model_id} value={m.model_id}>{m.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Optional instructions */}
      <textarea
        value={userPrompt}
        onChange={(e) => setUserPrompt(e.target.value)}
        placeholder="Additional instructions (optional) — e.g. 'Focus on tone' or 'Was the tool usage efficient?'"
        rows={2}
        className="w-full text-xs bg-ui-input border border-ui-border rounded-lg px-2.5 py-1.5 text-ui-text placeholder:text-ui-text3 resize-none focus:outline-none focus:ring-1 focus:ring-brand-dark"
      />

      {/* Run button */}
      <button
        onClick={handleRun}
        disabled={!selectedModel || analyzing}
        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-dark text-white disabled:opacity-40 hover:opacity-90 transition"
      >
        {analyzing ? 'Analyzing…' : '⚡ Run Analysis'}
      </button>

      {/* Result */}
      {result !== null && (
        <div className="bg-ui-subtle border border-ui-border rounded-lg p-3 max-h-64 overflow-y-auto">
          <p className="text-[11px] font-semibold text-ui-text2 mb-1.5">Analysis Result</p>
          <p className="text-xs text-ui-text whitespace-pre-wrap leading-relaxed">
            {result || '(Model returned no content — try a different model.)'}
          </p>
        </div>
      )}
    </div>
  );
};

// ── Conversation detail panel ─────────────────────────────────────────────────

function stepsToLines(steps: AgentRunStep[]): string[] {
  const lines: string[] = [];
  for (const s of steps) {
    const t = s.step_type;
    const meta = s.metadata ?? {};
    const content = s.content as Record<string, unknown> | string | null;

    if (t === 'user_input') {
      const text = typeof content === 'string' ? content : (content as Record<string, string>)?.text ?? '';
      if (text) lines.push(`  [User Input] ${text}`);

    } else if (t === 'agent_response') {
      const text = typeof content === 'string' ? content : (content as Record<string, string>)?.text ?? '';
      if (text) lines.push(`  [Agent Response] ${text}`);

    } else if (t === 'llm_call') {
      // content = { model, prompt: [{role, content}...] }
      const c = content as Record<string, unknown>;
      const msgs = (c?.prompt ?? meta?.messages ?? []) as Array<Record<string, unknown>>;
      const model = (c?.model ?? '') as string;
      lines.push(`  [LLM Call] model=${model} ${msgs.length} message(s)`);
      for (const m of msgs) {
        const role = (m.role ?? '') as string;
        let text = m.content ?? '';
        if (Array.isArray(text)) text = (text as Array<Record<string, string>>).map((p) => p.text ?? '').join(' ');
        if (text) lines.push(`    [${role}]: ${text}`);
      }

    } else if (t === 'tool_call') {
      const name = (meta?.tool_name ?? (content as Record<string, unknown>)?.name ?? 'tool') as string;
      const args = meta?.args ?? (content as Record<string, unknown>)?.args ?? {};
      lines.push(`  [Tool Call] ${name}(${JSON.stringify(args)})`);

    } else if (t === 'tool_output') {
      const name = (meta?.tool_name ?? '') as string;
      const out = typeof content === 'object' ? JSON.stringify(content) : String(content ?? '');
      lines.push(`  [Tool Output] ${name}: ${out}`);

    } else if (t === 'agent_action') {
      const title = (meta?.title ?? meta?.caption ?? '') as string;
      const mod = (meta?.module ?? '') as string;
      lines.push(`  [Agent Action] ${mod}: ${title}`);
    }
  }
  return lines;
}

function buildExportPayload(
  convo: PlatformConversation,
  messages: PlatformChatMessage[],
  feedback: Record<string, unknown>,
  steps: AgentRunStep[],
) {
  return {
    conversation_id: convo.session_id,
    user: convo.user_name || convo.user_id,
    tenant_id: convo.tenant_id,
    agent_id: convo.agent_id,
    messages: messages.map((m) => ({
      role: m.role,
      content: m.content,
      created_at: m.created_at,
      feedback: feedback[m.message_id] ?? null,
    })),
    agent_steps: steps,
  };
}

function buildCopyText(
  convo: PlatformConversation,
  messages: PlatformChatMessage[],
  feedback: Record<string, unknown>,
  steps: AgentRunStep[],
) {
  const lines: string[] = [
    `Conversation: ${convo.session_id}`,
    `User: ${convo.user_name || convo.user_id}`,
    `Tenant: ${convo.tenant_id}`,
    `Agent: ${convo.agent_id}`,
    '',
  ];
  for (const m of messages) {
    lines.push(`[${m.role === 'user' ? 'User' : 'Agent'}] ${fmtDate(m.created_at)}`);
    lines.push(m.content || '(empty)');
    const fb = feedback[m.message_id] as { helpful?: boolean; comment?: string; created_at?: string } | undefined;
    if (fb) {
      lines.push(`Feedback: ${fb.helpful ? '👍 Positive' : '👎 Negative'}${fb.comment ? ` — "${fb.comment}"` : ''}`);
    }
    lines.push('');
  }
  if (steps.length > 0) {
    lines.push('=== Agent Steps ===');
    lines.push(...stepsToLines(steps));
    lines.push('');
  }
  return lines.join('\n');
}

const ConversationDetail: React.FC<{
  conversationId: string;
  feedbackEntry?: ChatFeedbackEntry | null;
}> = ({ conversationId, feedbackEntry }) => {
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const feedbackMsgRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Always load the full conversation — messageId is used only for badge/scroll positioning,
  // not to narrow the message list, so the admin sees full context.
  const { data, isLoading, isError } = useGetPlatformChatConversationQuery({
    conversationId,
  });
  const convo: PlatformConversation | undefined = data?.data;

  const messages: PlatformChatMessage[] = convo?.messages ?? [];
  const assistantMsg = messages.find((m) => m.role === 'assistant');

  // Eagerly fetch all runs so Copy/Export always include the full session steps
  const { data: runsData } = useGetPlatformConversationRunsQuery(
    { conversationId },
    { skip: !conversationId },
  );
  const runs = (runsData?.data ?? []) as { steps?: AgentRunStep[] }[];
  const steps: AgentRunStep[] = runs.flatMap((r) => r.steps ?? []);

  // After messages load, scroll to the message carrying the feedback badge so the admin
  // doesn't have to hunt through a long conversation (especially voice/realtime sessions
  // where the full transcript is returned and the badge is near the bottom).
  // Must be before any conditional return to satisfy Rules of Hooks.
  useEffect(() => {
    if (feedbackMsgRef.current) {
      feedbackMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (scrollContainerRef.current) {
      // No specific message matched (e.g. old voice session) — snap to top so the banner is visible.
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [messages.length, feedbackEntry?.feedback_id]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full text-ui-text3 text-sm">Loading…</div>;
  }
  if (isError || !convo) {
    return <div className="flex items-center justify-center h-full text-red-500 text-sm">Failed to load conversation.</div>;
  }

  const feedback = convo.feedback ?? {};

  // When multiple feedback entries exist in the same session (common for long voice sessions),
  // the backend re-keys all of them into `feedback`. We must show ONLY the selected entry's
  // badge, identified by feedback_id rather than message key.
  type FeedbackDoc = { feedback_id?: string; helpful?: boolean; comment?: string | null; created_at?: string };

  // feedback_id_to_msg: collision-free reverse index from the backend.
  // Maps feedback_id → reconstructed message_id so we can target the exact message even when
  // two feedbacks share the same reconstructed step (timestamp collision in recon_feedback).
  type FeedbackIdToMsg = Record<string, string>;
  const feedbackIdToMsg = ((convo as unknown as Record<string, unknown>).feedback_id_to_msg ?? {}) as FeedbackIdToMsg;

  // The reconstructed message_id that the selected feedback entry belongs to (if known).
  const targetMsgId = feedbackEntry?.feedback_id ? feedbackIdToMsg[feedbackEntry.feedback_id] : undefined;
  const feedbackAttachedToAMessage = !!targetMsgId && messages.some((m) => m.message_id === targetMsgId);

  const handleCopy = () => {
    const text = buildCopyText(convo, messages, feedback, steps);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = () => {
    const payload = buildExportPayload(convo, messages, feedback, steps);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-feedback-${conversationId.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-ui-border2 flex-shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ui-text truncate">
              {convo.user_name || shortId(convo.user_id)}
            </p>
            {convo.user_email && (
              <p className="text-xs text-ui-text3 mt-0.5 truncate">{convo.user_email}</p>
            )}
            <p className="text-[11px] font-mono text-ui-text3 mt-0.5 truncate">{convo.session_id}</p>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-ui-text3">
              <span>tenant: <strong className="text-ui-text2">{shortId(convo.tenant_id)}</strong></span>
              <span>agent: <strong className="text-ui-text2">{convo.agent_id ?? '—'}</strong></span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setSpecOpen((o) => !o)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition ${
                specOpen
                  ? 'bg-ui-hover text-ui-text border-ui-border2'
                  : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
              }`}
              title="Session spec"
            >
              ⓘ Info
            </button>
            <button
              onClick={() => setAnalyzeOpen((o) => !o)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition ${
                analyzeOpen
                  ? 'bg-brand-dark text-white border-brand-dark'
                  : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
              }`}
            >
              ⚡ Analyze
            </button>
            <button
              onClick={handleCopy}
              className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-ui-subtle text-ui-text2 border border-ui-border hover:bg-ui-hover transition"
            >
              {copied ? '✓ Copied' : '📋 Copy'}
            </button>
            <button
              onClick={handleExport}
              className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-ui-subtle text-ui-text2 border border-ui-border hover:bg-ui-hover transition"
            >
              ⬇ Export
            </button>
          </div>
        </div>

        {/* Collapsible session spec panel */}
        {specOpen && (
          <div className="mt-3 space-y-3 bg-ui-subtle border border-ui-border2 rounded-xl px-4 py-3 text-xs">
            {/* Profile row */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              <div>
                <span className="text-ui-text3">User</span>
                <p className="text-ui-text font-medium truncate">{convo.user_name || '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Email</span>
                <p className="text-ui-text truncate">{convo.user_email || feedbackEntry?.user_email || '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Agent</span>
                <p className="text-ui-text font-mono truncate">{convo.agent_id || '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Total messages</span>
                <p className="text-ui-text">{convo.total_messages ?? '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Session started</span>
                <p className="text-ui-text">{fmtDate(convo.created_at)}</p>
              </div>
              <div>
                <span className="text-ui-text3">Last activity</span>
                <p className="text-ui-text">{fmtDate(convo.last_activity_at)}</p>
              </div>
              {feedbackEntry && (
                <>
                  <div>
                    <span className="text-ui-text3">Feedback</span>
                    <p className="text-ui-text font-medium">{feedbackEntry.helpful ? '👍 Positive' : '👎 Negative'}</p>
                  </div>
                  <div>
                    <span className="text-ui-text3">Submitted</span>
                    <p className="text-ui-text">{fmtDate(feedbackEntry.created_at)}</p>
                  </div>
                  {feedbackEntry.comment && (
                    <div className="col-span-2">
                      <span className="text-ui-text3">Comment</span>
                      <p className="text-ui-text mt-0.5 italic">"{feedbackEntry.comment}"</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* DB refs — click any row to copy the full ID */}
            <div className="border-t border-ui-border2 pt-2.5 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-ui-text3 mb-1.5">DB refs (click to copy)</p>
              {(
                [
                  { label: 'session_id', value: convo.session_id, hint: 'sessions / conversation_runs' },
                  { label: 'user_id',    value: convo.user_id,    hint: 'users' },
                  { label: 'tenant_id',  value: convo.tenant_id,  hint: 'tenants' },
                  ...(feedbackEntry ? [
                    { label: 'feedback_id', value: feedbackEntry.feedback_id, hint: 'message_feedback' },
                    { label: 'message_id',  value: feedbackEntry.message_id,  hint: 'message_feedback / run step key' },
                  ] : []),
                  ...(assistantMsg ? [
                    { label: 'run_id', value: assistantMsg.message_id.split(':')[0], hint: 'conversation_runs' },
                  ] : []),
                ] as { label: string; value: string | null | undefined; hint: string }[]
              ).map(({ label, value, hint }) => (
                <button
                  key={label}
                  title={`${hint} — click to copy`}
                  disabled={!value}
                  onClick={() => value && navigator.clipboard?.writeText(value).catch(() => {})}
                  className="w-full flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-ui-hover transition text-left disabled:opacity-40 disabled:cursor-default"
                >
                  <span className="w-24 flex-shrink-0 text-ui-text3">{label}</span>
                  <span className="font-mono text-ui-text truncate flex-1">{value || '—'}</span>
                  <span className="text-ui-text3 flex-shrink-0 opacity-50">⎘</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message list */}
      <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
        {/* Banner for voice sessions where exact message can't be pinpointed */}
        {!feedbackAttachedToAMessage && feedbackEntry && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl px-3 py-2.5 flex items-start gap-2">
            <span className="text-base flex-shrink-0">{feedbackEntry.helpful ? '👍' : '👎'}</span>
            <div className="min-w-0">
              <span className="text-xs font-medium text-amber-800 dark:text-amber-300">
                {feedbackEntry.helpful ? 'Positive feedback' : 'Negative feedback'}
                <span className="text-[10px] text-ui-text3 ml-2">· {fmtDate(feedbackEntry.created_at)}</span>
              </span>
              {feedbackEntry.comment && (
                <p className="text-[11px] text-ui-text2 mt-0.5">{feedbackEntry.comment}</p>
              )}
              <p className="text-[10px] text-ui-text3 mt-1 italic">Specific message not identified — showing full conversation.</p>
            </div>
          </div>
        )}
        {messages.length === 0 && (
          <p className="text-xs text-ui-text3 text-center py-8">Message not found in conversation.</p>
        )}
        {messages.map((msg) => {
          const isUser = msg.role === 'user';
          // Show the badge only on the exact message the selected feedback targets.
          // Use feedbackEntry directly (not rawFb) so collisions in recon_feedback don't matter.
          const msgFeedback: FeedbackDoc | undefined =
            msg.message_id === targetMsgId && feedbackEntry
              ? { helpful: feedbackEntry.helpful, comment: feedbackEntry.comment, created_at: feedbackEntry.created_at }
              : undefined;

          return (
            <div
              key={msg.message_id}
              ref={msg.message_id === targetMsgId ? feedbackMsgRef : undefined}
              className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  isUser
                    ? 'bg-brand-dark text-white'
                    : msgFeedback
                    ? 'bg-ui-subtle border border-amber-400 ring-1 ring-amber-400/50 text-ui-text'
                    : 'bg-ui-subtle border border-ui-border text-ui-text'
                }`}
              >
                {msg.content || <span className="italic text-xs opacity-60">(empty)</span>}
              </div>

              {msg.created_at && (
                <span className="mt-0.5 px-1 text-[10px] text-ui-text3">{fmtDate(msg.created_at)}</span>
              )}

              {!isUser && msgFeedback && (
                <div className="flex items-start gap-1.5 mt-1.5 ml-1 max-w-[80%] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg px-2.5 py-1.5">
                  <span className="text-base flex-shrink-0">{msgFeedback.helpful ? '👍' : '👎'}</span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] font-medium text-amber-800 dark:text-amber-300">
                      {msgFeedback.helpful ? 'Positive feedback' : 'Negative feedback'}
                    </span>
                    {msgFeedback.comment && (
                      <span className="text-[11px] text-ui-text2 mt-0.5">{msgFeedback.comment}</span>
                    )}
                    <span className="text-[10px] text-ui-text3 mt-0.5">{fmtDate(msgFeedback.created_at)}</span>
                  </div>
                </div>
              )}

              {!isUser && (
                <PlatformTurnSteps
                  conversationId={conversationId}
                  targetStepId={msg.message_id === targetMsgId ? targetMsgId : undefined}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Analyze panel — shown below messages when toggled */}
      {analyzeOpen && assistantMsg && (
        <AnalyzePanel
          conversationId={conversationId}
          messageId={assistantMsg.message_id}
        />
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const ChatMonitorPage: React.FC = () => {
  const [filterHelpful, setFilterHelpful] = useState<boolean | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState<ChatFeedbackEntry | null>(null);
  const limit = 50;

  const { data, isLoading } = useListPlatformChatFeedbackQuery({
    helpful: filterHelpful,
    limit,
    offset,
  });

  const entries: ChatFeedbackEntry[] = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex h-full min-h-0">
      {/* ── Left: feedback list ── */}
      <div className="w-80 flex-shrink-0 border-r border-ui-border flex flex-col bg-ui-card">
        <div className="p-3 border-b border-ui-border2 space-y-2">
          <h2 className="text-sm font-semibold text-ui-text2">Chat Feedback</h2>
          <div className="flex gap-1.5">
            {(
              [
                { label: 'All', value: undefined },
                { label: '👍 Positive', value: true },
                { label: '👎 Negative', value: false },
              ] as { label: string; value: boolean | undefined }[]
            ).map(({ label, value }) => (
              <button
                key={label}
                onClick={() => { setFilterHelpful(value); setOffset(0); }}
                className={`px-2 py-0.5 rounded-full text-[11px] border transition ${
                  filterHelpful === value
                    ? 'bg-brand-dark text-white border-brand-dark'
                    : 'bg-ui-subtle text-ui-text2 border-ui-border hover:bg-ui-hover'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-ui-text3">{total} feedback entries</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && <p className="text-xs text-ui-text3 p-4">Loading…</p>}
          {!isLoading && entries.length === 0 && (
            <p className="text-xs text-ui-text3 p-4">No feedback found.</p>
          )}
          {entries.map((entry) => (
            <button
              key={entry.feedback_id}
              onClick={() => setSelectedFeedback(entry)}
              className={`w-full text-left px-3 py-2.5 border-b border-ui-border2 hover:bg-ui-hover transition ${
                selectedFeedback?.feedback_id === entry.feedback_id
                  ? 'bg-blue-50 dark:bg-brand-dark/10 border-l-2 border-l-[#1c1849] dark:border-l-brand-dark'
                  : ''
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">{entry.helpful ? '👍' : '👎'}</span>
                <p className="text-xs font-medium text-ui-text truncate flex-1">
                  {entry.user_name || shortId(entry.user_id)}
                </p>
                <span className="text-[10px] text-ui-text3 flex-shrink-0">
                  {fmtDate(entry.created_at).split(',')[0]}
                </span>
              </div>
              <p className="text-[11px] text-ui-text2 truncate mt-0.5">
                {entry.session_title || shortId(entry.session_id)}
              </p>
              {entry.comment && (
                <p className="text-[11px] text-ui-text3 truncate italic mt-0.5">"{entry.comment}"</p>
              )}
            </button>
          ))}
        </div>

        {total > limit && (
          <div className="p-2 border-t border-ui-border2 flex items-center justify-between text-xs text-ui-text2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
              className="disabled:opacity-40 hover:text-[#1c1849]"
            >
              ← Prev
            </button>
            <span>{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
            <button
              disabled={offset + limit >= total}
              onClick={() => setOffset((o) => o + limit)}
              className="disabled:opacity-40 hover:text-[#1c1849]"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ── Right: conversation detail ── */}
      <div className="flex-1 min-w-0 bg-ui-raised">
        {selectedFeedback ? (
          <ConversationDetail
            conversationId={selectedFeedback.session_id}
            feedbackEntry={selectedFeedback}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-ui-text3 text-sm">
            Select a feedback entry to view the conversation.
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMonitorPage;
